import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import Header, { AccountTypeBadge } from '../../components/Header';
import ReviewForm from '../../components/ReviewForm';
import ReviewModal from '../../components/ReviewModal';
import ServiceChat from '../../components/ServiceChat';
import { PaymentStatusCard } from '../../components/PaymentStatus';
import { LocationPinUpdater } from '../../components/LocationTracker';
import { BottomActionBar, QuickActionButtons, ETADisplay, ResponsiveContainer, useMobileView } from '../../components/MobileLayout';
import LiveTrackingMap, { MobileLiveTracking } from '../../components/LiveTrackingMap';
import TippingDonation from '../../components/TippingDonation';
import PricingBreakdown from '../../components/PricingBreakdown';
import { StripeCheckoutModal } from '../../components/StripeCheckout';
import { CheckrBadge } from '../../components/JobGallery';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  ArrowLeft, Phone, MessageCircle, Clock, MapPin, User, 
  Truck, Battery, Car, Key, Wrench, Shield, Loader2, X, CreditCard,
  Star, Calendar, Navigation, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const mechanicIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const customerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const serviceIcons = {
  tow: Truck,
  jump_start: Battery,
  flat_tire: Car,
  lockout: Key,
  repair: Wrench,
  diagnostic: Shield
};

const statusSteps = [
  { status: 'awaiting_payment', label: 'Payment Required' },
  { status: 'pending', label: 'Waiting for Mechanic' },
  { status: 'accepted', label: 'Mechanic Accepted' },
  { status: 'en_route', label: 'Mechanic En Route' },
  { status: 'arrived', label: 'Mechanic Arrived' },
  { status: 'in_progress', label: 'Service In Progress' },
  { status: 'completed', label: 'Service Completed' }
];

export default function Tracking() {
  const { requestId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useMobileView();
  
  const [request, setRequest] = useState(null);
  const [providerDetails, setProviderDetails] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [eta, setEta] = useState(null);
  const [showChat, setShowChat] = useState(false);

  // Fetch ETA from server
  const fetchEta = useCallback(async () => {
    if (!request?.mechanic_id) return;
    try {
      const response = await axios.get(`${API}/services/${requestId}/eta`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setEta(response.data);
    } catch (error) {
      console.error('ETA fetch error:', error);
    }
  }, [requestId, request?.mechanic_id, getAuthHeaders]);

  useEffect(() => {
    fetchRequest();
    const interval = setInterval(fetchRequest, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  // Fetch ETA periodically when provider is assigned
  useEffect(() => {
    if (request?.mechanic_id && !['completed', 'cancelled'].includes(request?.status)) {
      fetchEta();
      const etaInterval = setInterval(fetchEta, 15000); // Update ETA every 15 seconds
      return () => clearInterval(etaInterval);
    }
  }, [request?.mechanic_id, request?.status, fetchEta]);

  // Auto-show review modal on completion or payment
  useEffect(() => {
    if (request?.status === 'completed' || request?.payment_status === 'paid') {
      // Check if user hasn't reviewed yet
      if (!request?.customer_reviewed && request?.mechanic_id) {
        // Delay popup slightly for better UX
        const timer = setTimeout(() => setShowReviewModal(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [request?.status, request?.payment_status, request?.customer_reviewed, request?.mechanic_id]);

  // Fetch provider details when mechanic is assigned
  useEffect(() => {
    if (request?.mechanic_id) {
      fetchProviderDetails(request.mechanic_id);
      fetchExistingReview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.mechanic_id]);

  const fetchProviderDetails = async (providerId) => {
    try {
      const response = await axios.get(
        `${API}/providers/${providerId}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setProviderDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch provider details:', error);
    }
  };

  const fetchExistingReview = async () => {
    try {
      const response = await axios.get(
        `${API}/reviews/service/${requestId}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setExistingReview(response.data);
    } catch (error) {
      // 404 from /reviews/service is the normal "no review yet" case.
      // Log unexpected errors at debug level so they're visible if needed.
      if (error?.response?.status && error.response.status !== 404) {
        console.debug('fetchExistingReview failed', error);
      }
    }
  };

  // Calculate ETA based on mechanic location
  const calculateETA = () => {
    if (!request?.mechanic_location || !request?.location) return null;
    const distance = Math.sqrt(
      Math.pow(request.mechanic_location.lat - request.location.lat, 2) +
      Math.pow(request.mechanic_location.lng - request.location.lng, 2)
    );
    // Rough estimate: 1 degree ~ 69 miles, average speed 30mph
    const miles = distance * 69;
    const minutes = Math.round((miles / 30) * 60);
    return Math.max(5, Math.min(60, minutes)); // Between 5-60 min
  };

  const fetchRequest = async () => {
    try {
      const response = await axios.get(
        `${API}/services/${requestId}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setRequest(response.data);
    } catch (error) {
      console.error('Failed to fetch request:', error);
      toast.error('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const hasProvider = request?.mechanic_id;
    const confirmMessage = hasProvider 
      ? 'A provider has been assigned. Cancelling now will incur a $7 cancellation fee. Continue?'
      : 'Are you sure you want to cancel this request?';
    
    if (!window.confirm(confirmMessage)) return;
    
    setCancelling(true);
    try {
      const response = await axios.put(
        `${API}/services/${requestId}/cancel`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      if (response.data.requires_payment && response.data.checkout_url) {
        // Redirect to Stripe to pay cancellation fee
        toast.info('Redirecting to pay cancellation fee...');
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Request cancelled');
        navigate('/customer');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setCancelling(false);
    }
  };

  const handleStripePayment = async () => {
    setPaymentLoading(true);
    try {
      // Create Stripe checkout session
      const response = await axios.post(
        `${API}/stripe/checkout/service`,
        { 
          request_id: requestId,
          origin_url: window.location.origin 
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start payment. Please try again.');
      setPaymentLoading(false);
    }
  };

  const getStatusIndex = (status) => {
    return statusSteps.findIndex(s => s.status === status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[70vh]">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Request Not Found</h2>
          <Button onClick={() => navigate('/customer')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const ServiceIcon = serviceIcons[request.service_type] || Car;
  const currentStatusIndex = getStatusIndex(request.status);
  const isCancelled = request.status === 'cancelled';
  const isCompleted = request.status === 'completed';
  const canCancel = ['pending', 'accepted', 'awaiting_payment'].includes(request.status);
  // Show payment button if: awaiting payment status OR (completed but not paid)
  const needsPayment = (request.status === 'awaiting_payment' || (isCompleted && request.payment_status !== 'paid')) && request.payment_status !== 'paid';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="tracking-page">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button with Account Badge */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/customer')}
            className="flex items-center text-gray-600 hover:text-gray-900"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <AccountTypeBadge 
            userType={user?.user_type} 
            isAdmin={user?.is_admin} 
            isOwner={user?.is_owner}
            size="md"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Map Section - Enhanced Live Tracking */}
          <Card className="overflow-hidden" data-testid="tracking-map">
            {isMobile ? (
              // Mobile-optimized tracking view
              <div className="p-0">
                <LiveTrackingMap
                  customerLocation={request.location}
                  providerLocation={request.mechanic_location}
                  providerName={request.mechanic_name}
                  providerPhone={providerDetails?.phone}
                  serviceStatus={request.status}
                  onRefresh={fetchRequest}
                />
              </div>
            ) : (
              // Desktop view with legacy map + enhancements
              <div className="h-[400px]">
                {request.location?.lat && (
                  <LiveTrackingMap
                    customerLocation={request.location}
                    providerLocation={request.mechanic_location}
                    providerName={request.mechanic_name}
                    providerPhone={providerDetails?.phone}
                    serviceStatus={request.status}
                    onRefresh={fetchRequest}
                    className="h-full"
                  />
                )}
              </div>
            )}
            
            {/* Location Update Section */}
            {!isCancelled && !isCompleted && (
              <div className="p-4 border-t">
                <LocationPinUpdater 
                  requestId={requestId}
                  onLocationUpdate={(loc) => {
                    setRequest(prev => ({
                      ...prev,
                      location: { ...prev.location, lat: loc.lat, lng: loc.lng, address: loc.address }
                    }));
                  }}
                />
              </div>
            )}
          </Card>

          {/* Status Section */}
          <div className="space-y-6">
            {/* Service Info Card */}
            <Card data-testid="service-info-card">
              <CardContent className="py-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ServiceIcon className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 capitalize font-['Chivo']">
                      {request.service_type.replace('_', ' ')}
                    </h2>
                    <p className="text-gray-600">
                      {request.vehicle_year} {request.vehicle_make} {request.vehicle_model}
                    </p>
                  </div>
                  {/* ETA Display */}
                  {request.mechanic_id && eta && !isCancelled && !isCompleted && (
                    <ETADisplay 
                      etaMinutes={eta.eta_minutes}
                      etaText={eta.eta_text}
                      distanceMiles={eta.distance_miles}
                      status={request.status}
                    />
                  )}
                </div>

                {isCancelled ? (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
                    <X className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-red-700 font-semibold">Request Cancelled</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {statusSteps.map((step, idx) => (
                      <div 
                        key={step.status}
                        className={`flex items-center gap-3 ${idx <= currentStatusIndex ? 'text-blue-600' : 'text-gray-400'}`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          idx < currentStatusIndex ? 'bg-green-500' : 
                          idx === currentStatusIndex ? 'bg-blue-600' : 'bg-gray-200'
                        }`}>
                          {idx < currentStatusIndex ? (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : idx === currentStatusIndex ? (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          ) : null}
                        </div>
                        <span className={`text-sm font-medium ${
                          idx === currentStatusIndex ? 'text-gray-900' : ''
                        }`}>
                          {step.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Mechanic Info */}
            {request.mechanic_name && (
              <Card data-testid="mechanic-info-card">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-500">YOUR SERVICE PROVIDER</h3>
                    <CheckrBadge size="sm" />
                  </div>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="relative w-16 h-16 flex-shrink-0">
                      <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-lg">
                        {providerDetails?.picture ? (
                          <img 
                            src={providerDetails.picture.startsWith('/api') ? `${API}${providerDetails.picture}` : providerDetails.picture} 
                            alt={request.mechanic_name} 
                            className="w-full h-full object-cover" 
                            data-testid="provider-avatar"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#FF3008] flex items-center justify-center">
                            <span className="text-white font-bold text-xl">{request.mechanic_name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-green-500 rounded-full border-2 border-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-lg" data-testid="provider-name">{request.mechanic_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center gap-1 bg-yellow-50 px-2 py-0.5 rounded-full" data-testid="provider-rating">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-bold text-gray-900">{providerDetails?.rating?.toFixed(1) || '5.0'}</span>
                        </div>
                        <span className="text-sm text-gray-500">{providerDetails?.completed_jobs || 0} jobs completed</span>
                      </div>
                    </div>
                  </div>

                  {/* Provider Details */}
                  <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                    {providerDetails?.experience_years && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{providerDetails.experience_years}+ years exp.</span>
                      </div>
                    )}
                    {providerDetails?.vehicle_type && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Truck className="w-4 h-4" />
                        <span>{providerDetails.vehicle_type}</span>
                      </div>
                    )}
                    {providerDetails?.specialties?.length > 0 && (
                      <div className="col-span-2 flex flex-wrap gap-1 mt-1">
                        {providerDetails.specialties.slice(0, 3).map((s) => (
                          <span key={s} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ETA */}
                  {request.status === 'en_route' && calculateETA() && (
                    <div className="p-3 bg-[#f0f4ff] border border-[#1a56db]/20 rounded-lg mb-4" data-testid="eta-display">
                      <div className="flex items-center gap-2 text-[#1a56db]">
                        <Navigation className="w-5 h-5" />
                        <span className="font-semibold">ETA: ~{calculateETA()} minutes</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      className="flex-1" 
                      data-testid="call-mechanic-btn"
                      onClick={() => window.location.href = `tel:${providerDetails?.phone || '2602183536'}`}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </Button>
                  </div>

                  {/* Real-time Chat */}
                  <div className="mt-4">
                    <ServiceChat
                      requestId={requestId}
                      otherPartyName={request.mechanic_name}
                      getAuthHeaders={getAuthHeaders}
                      userType="customer"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Price & Payment Status */}
            <Card data-testid="price-card">
              <CardContent className="py-6">
                {/* Payment Status Card - Always visible */}
                <PaymentStatusCard
                  customerPaymentStatus={request.payment_status}
                  providerPaymentStatus={request.provider_payment_status || 'pending'}
                  customerTotal={request.total_price || request.estimated_price}
                  providerPay={request.provider_pay}
                  showProviderInfo={false}
                  userType="customer"
                  autoChargeUrl={request.auto_charge_url}
                />
                
                {/* Pricing Breakdown - Show for unpaid requests */}
                {needsPayment && (
                  <div className="mt-4 border-t pt-4">
                    <PricingBreakdown
                      serviceType={request.service_type}
                      basePrice={request.estimated_price || 0}
                      partsCost={request.parts_cost || 0}
                      tipAmount={request.tips_total || 0}
                      donationAmount={request.donation_info?.total_donation || 0}
                      compact={true}
                    />
                  </div>
                )}
                
                {/* Tipping & Donation - Show before payment */}
                {needsPayment && (
                  <div className="mt-4 border-t pt-4">
                    <TippingDonation
                      serviceRequestId={requestId}
                      serviceTotal={request.total_price || request.estimated_price || 0}
                      onTipAdded={(total) => {
                        toast.success(`Tip added! Total: $${total.toFixed(2)}`);
                        fetchRequest();
                      }}
                      onDonationAdded={(amount) => {
                        if (amount > 0) fetchRequest();
                      }}
                      compact={true}
                    />
                  </div>
                )}
                
                {/* Pay Now Button for unpaid requests */}
                {needsPayment && (
                  <Button 
                    className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowPayment(true)}
                    data-testid="pay-now-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay Now - ${(request.total_price || request.estimated_price)?.toFixed(2)}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Cancel Button */}
            {canCancel && (
              <Button
                variant="outline"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleCancel}
                disabled={cancelling}
                data-testid="cancel-request-btn"
              >
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Cancel Request
              </Button>
            )}

            {/* Review Section */}
            {isCompleted && request.payment_status === 'paid' && !existingReview && !showReviewForm && (
              <Button
                className="w-full bg-yellow-500 hover:bg-yellow-600"
                onClick={() => setShowReviewForm(true)}
                data-testid="leave-review-btn"
              >
                <Star className="w-4 h-4 mr-2" />
                Leave a Review & Tip
              </Button>
            )}

            {showReviewForm && (
              <ReviewForm
                requestId={requestId}
                providerName={request.mechanic_name}
                onSubmit={() => {
                  setShowReviewForm(false);
                  fetchExistingReview();
                }}
                getAuthHeaders={getAuthHeaders}
              />
            )}

            {existingReview && (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= existingReview.rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">Your review</span>
                  </div>
                  {existingReview.comment && (
                    <p className="text-sm text-gray-700">{existingReview.comment}</p>
                  )}
                  {existingReview.tip_amount > 0 && (
                    <p className="text-sm text-green-600 mt-1">Tip: ${existingReview.tip_amount}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Embedded Stripe Payment Modal */}
      <StripeCheckoutModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        amount={request?.total_price || request?.estimated_price}
        requestId={requestId}
        itemName={`${request?.service_type?.replace('_', ' ')} Service`}
        onSuccess={() => {
          setShowPayment(false);
          fetchRequest();
          toast.success('Payment successful!');
        }}
        paymentType="service"
      />

      {/* Review Modal - Auto-popup on completion or payment */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        requestId={requestId}
        serviceName={request?.service_name || request?.service_type}
        otherPartyName={request?.mechanic_name || providerDetails?.name}
        otherPartyPicture={providerDetails?.picture}
        otherPartyId={request?.mechanic_id}
        reviewerType="customer"
        onSubmitted={() => {
          setShowReviewModal(false);
          fetchRequest();
        }}
      />

      {/* Bottom Action Bar - Mobile friendly */}
      {request?.mechanic_id && !isCancelled && !isCompleted && (
        <BottomActionBar>
          <QuickActionButtons
            phone={request?.mechanic_phone || providerDetails?.phone}
            onMessage={() => setShowChat(!showChat)}
            showLabels={!isMobile}
          />
        </BottomActionBar>
      )}
    </div>
  );
}
